import { VersionRange } from '../../models/core/version-range.js';
import {
  DataModelBuilder, isSameData, SerializationContext, SerializedData,
} from '../../models/dto/dto.js';
import { PhoneNumber } from '../../models/serializable/phone-number.js';
import { MapUtils } from '../../utility/map-utils.js';
import { Component } from '../core/component.js';

export class ContactComponent extends Component {
  static readonly TYPE_ID: string = '834392f9bbea47d9a3962b4bb41e4823';

  static readonly SCHEMA_VERSION: number = 5;

  private constructor(
    public readonly tikTok: string,
    public readonly facebook: string,
    public readonly instagram: string,
    public readonly website: string,
    public readonly emailAddress: string,
    public readonly phoneNumber: PhoneNumber | null,
    private readonly removable: boolean,
  ) {
    super();
  }

  static create(args: {
    tikTok: string,
    facebook: string,
    instagram: string,
    website: string,
    emailAddress: string,
    phoneNumber: PhoneNumber | null,
    removable: boolean,
  }): ContactComponent {
    return new ContactComponent(
        args.tikTok,
        args.facebook,
        args.instagram,
        args.website,
        args.emailAddress,
        args.phoneNumber,
        args.removable,
    );
  }

  get $typeId(): string {
    return ContactComponent.TYPE_ID;
  }

  get $schemaVersion(): VersionRange {
    return VersionRange.between(1, ContactComponent.SCHEMA_VERSION);
  }

  get $removable(): boolean {
    return this.removable;
  }

  static empty(removable = false): ContactComponent {
    return new ContactComponent(
        '',
        '',
        '',
        '',
        '',
        null,
        removable
    );
  }

  static fromMap(context: SerializationContext, map: SerializedData): ContactComponent {
    return new ContactComponent(
        MapUtils.get<string>(map, 'tikTok'),
        MapUtils.get<string>(map, 'facebook'),
        MapUtils.get<string>(map, 'instagram'),
        MapUtils.get<string>(map, 'website'),
        MapUtils.get<string>(map, 'emailAddress'),
        PhoneNumber.decode(MapUtils.getOrNull<string>(map, 'phoneNumber')),
        MapUtils.get<boolean>(map, 'removable'),
    );
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      'tikTok': this.tikTok,
      'facebook': this.facebook,
      'instagram': this.instagram,
      'website': this.website,
      'emailAddress': this.emailAddress,
      'phoneNumber': this.phoneNumber?.encode() ?? null,
      'removable': this.removable,
    };
  }

  clone(): this {
    return new ContactComponent(
        this.tikTok,
        this.facebook,
        this.instagram,
        this.website,
        this.emailAddress,
        this.phoneNumber?.clone() ?? null,
        this.removable,
    ) as this;
  }

  isSame(other: ContactComponent): boolean {
    if(this === other) {
      return true;
    }

    return other.tikTok === this.tikTok &&
      other.facebook === this.facebook &&
      other.instagram === this.instagram &&
      other.website === this.website &&
      other.emailAddress === this.emailAddress &&
      isSameData(other.phoneNumber, this.phoneNumber) &&
      other.removable === this.removable;
  }

  copyWith(args: Partial<Parameters<typeof ContactComponent.create>[0]>): ContactComponent {
    return ContactComponent.create({
      tikTok: args.tikTok ?? this.tikTok,
      facebook: args.facebook ?? this.facebook,
      instagram: args.instagram ?? this.instagram,
      website: args.website ?? this.website,
      emailAddress: args.emailAddress ?? this.emailAddress,
      phoneNumber: args.phoneNumber ?? this.phoneNumber?.clone() ?? null,
      removable: args.removable ?? this.removable,
    });
  }
}

export class ContactComponentBuilder extends DataModelBuilder<ContactComponent> {
  build(context: SerializationContext, map: SerializedData): ContactComponent {
    return ContactComponent.fromMap(context, map);
  }

  migrateToCurrentSchemaVersion(context: SerializationContext, map: SerializedData): SerializedData | null {
    const versionRange = MapUtils.getVersionRange(map);
    if(versionRange.contains(ContactComponent.SCHEMA_VERSION)) {
      return map;
    }
    if(versionRange.isGreaterThan(ContactComponent.SCHEMA_VERSION)) {
      return null; // This data comes from the FUTURE. We have no idea how to migrate it.
    }

    let version = versionRange.to;

    // In this version `emailAddress` of type [String] was introduced.
    if(version < 2) {
      MapUtils.setIfNullOrUndefined(map, 'emailAddress', '');

      version = 2;
    }

    // In this version `tikTok` of type [String] was introduced.
    if(version < 3) {
      MapUtils.setIfNullOrUndefined(map, 'tikTok', '');

      version = 3;
    }

    // In this version `phoneNumber` of type [PhoneNumber] was introduced.
    if(version < 4) {
      MapUtils.setIfNullOrUndefined(map, 'phoneNumber', null);

      version = 4;
    }

    // In this version `removable` of type [bool] was introduced.
    if(version < 5) {
      MapUtils.setIfNullOrUndefined(map, 'removable', false);

      version = 5;
    }

    map['$schema_version'] = VersionRange.encodedExactly(version);
    return map;
  }
}
